import {
  createSelector,
} from 'reselect';

import {
  getProfile,
} from './profile';
import {
  getResourceById,
  getResourceMappedList,
} from './resources';
import {
  onlyUniqueId,
  alphSortByName,
  alphSortSearchOptions,
  getActivityText,
  getActivityTime,
} from './utils';

import type {
  ResourceType,
} from '../types';

export const getSearchOptions = (resourceType: ResourceType, list?: string) => createSelector(
  getResourceMappedList(resourceType, list),
  (options) => {
    switch (resourceType) {
      case 'clients':
      case 'archivedClients':
        return options
          .filter(item => !item.is_pro)
          .map(option => ({
            label: `${option.user_profile.first_name} ${option.user_profile.last_name}`,
            value: String(option.id),
            rest: option,
          }))
          .sort(alphSortSearchOptions);
      default:
        return options
          .map(({ id, name }) => ({
            label: name,
            value: String(id),
          }))
          .sort(alphSortSearchOptions);
    }
  },
);

export const getCalendarClients = createSelector(
  getResourceMappedList('clients'),
  clients => clients.map(
    client => ({
      label: `${client.user_profile.first_name} ${client.user_profile.last_name}`,
      value: String(client.id),
    }),
  ),
);

export const getClientCountAllowed = createSelector(
  [
    getProfile,
    getResourceMappedList('clients'),
  ],
  (profile, clients) => {
    const clientsCount = clients.filter(c => (!c.is_pro && !c.invited_by_id)).length;
    if (profile.payment_bypass) return 99999;
    if (!profile.user_settings?.max_clients_count) return 99999;
    return profile?.user_settings?.max_clients_count - clientsCount;
  },
);

export const getClientTemplates = (
  clientId: string | number,
  resourceType: ResourceType,
) => createSelector(
  getResourceMappedList(resourceType, `client_${clientId}`),
  (templates) => {
    switch (resourceType) {
      case 'personalWorkouts':
        return templates
          .filter(item => !item.is_for_single_exercise)
          .sort(alphSortByName);
      case 'personalPrograms':
        return templates
          .sort(alphSortByName);
      case 'documents':
        return templates
          .filter(onlyUniqueId)
          .sort(alphSortByName);
      default:
        return templates
          .sort(alphSortByName);
    }
  },
);

export const getClientInfo = clientId => createSelector(
  [
    getResourceById('clients', clientId),
    getResourceMappedList('userAuthentications', `client_${clientId}`),
    getResourceMappedList('videos', `${clientId}`),
    getProfile,
  ],
  (
    clientInfo,
    authentications,
    videos,
    profile,
  ) => ({
    ...(profile.id === clientId
      ? profile
      : clientInfo
    ),
    videos,
    authentications,
  }),
);

export const getSearchClientOptions = (resourceType: ResourceType, list = 'all') => createSelector(
  getResourceMappedList(resourceType, list),
  options => options.map(
    option => ({
      label: option.name,
      value: String(option.id),
    }),
  ).sort(alphSortSearchOptions),
);

const assigneesSelectors = {};
export const getTemplateAssignees = (listName: string) => {
  if (assigneesSelectors[listName]) {
    return assigneesSelectors[listName];
  }

  assigneesSelectors[listName] = (
    createSelector(
      [
        getProfile,
        getResourceMappedList('clients', 'clientList'),
        getResourceMappedList('clientGroups'),
        getResourceMappedList('assignees', listName),
        getResourceMappedList('groupAssignments', listName),
      ],
      (
        me,
        clients,
        clientGroups,
        assignees,
        groupAssignments,
      ) => {
        const assigned = (client) => ({
          ...client,
          ...(assignees.some(a => a.person_id === client.id)
            ? {
              ...assignees.find(a => a.person_id === client.id),
              is_assigned: true,
            } : {
              is_assigned: false,
            }
          ),
          is_client: true,
          avatar: client.user_profile?.avatar?.thumb?.url,
          name: `${client.user_profile.first_name} ${client.user_profile.last_name}`,
        })

        return {
          groups: (
            clientGroups
              .map(
                (group) => {
                  let assigned = false;
                  let assignedCount = 0;
                  const assigment = groupAssignments.find(g => g.id === group.id);

                  if (assigment) {
                    assignedCount = assigment.assigned_count;
                    assigned = assignedCount > 0;
                  }

                  return {
                    ...group,
                    avatar: group.avatar?.thumb?.url,
                    is_assigned: assigned,
                    assigned_count: assignedCount,
                  };
                },
              )
              .sort(
                alphSortByName,
              )
          ),
          individuals: [
            {
              ...assigned(me),
              name: 'me',
            },
            ...clients
              .map(assigned)
              .sort(
                alphSortByName,
              )
          ],
        };
      },
    )
  );
  return assigneesSelectors[listName];
};

export const getDocumentAssignees = (listName: string) => {
  if (assigneesSelectors[listName]) {
    return assigneesSelectors[listName];
  }
  assigneesSelectors[listName] = (
    createSelector(
      [
        getProfile('id'),
        getResourceMappedList('clients', 'clientList'),
        getResourceMappedList('clientGroups'),
        getResourceMappedList('assignees', listName),
        getResourceMappedList('groupAssignments', listName),
      ],
      (
        userId,
        clients,
        clientGroups,
        assignees,
        groupAssignments,
      ) => ({
        groups: (
          clientGroups
            .map(
              (group) => {
                let assigned = false;
                let assignedCount = 0;
                const assigment = groupAssignments.find(g => g.id === group.id);

                if (assigment) {
                  assignedCount = assigment.clients_count;
                  assigned = assignedCount > 0;
                }

                return {
                  ...group,
                  avatar: group.avatar?.thumb?.url,
                  is_assigned: assigned,
                  assigned_count: assignedCount,
                };
              },
            )
            .sort(
              alphSortByName,
            )
        ),
        individuals: (
          clients
            .map(
              client => ({
                ...client,
                ...(assignees.some(a => a.user_id === client.id)
                  ? {
                    ...assignees.find(a => a.user_id === client.id),
                    is_assigned: true,
                  } : {
                    is_assigned: false,
                  }
                ),
                ...(client.id === userId
                  ? {
                    name: 'me',
                  } : {
                    name: `${client.user_profile.first_name} ${client.user_profile.last_name}`,
                  }
                ),
                is_client: true,
                avatar: client.user_profile?.avatar?.thumb?.url,
              }),
            )
            .sort(
              alphSortByName,
            )
        ),
      }),
    )
  );
  return assigneesSelectors[listName];
};

export const getRecentActivity = userId => createSelector(
  [
    getResourceMappedList('workoutEvents', String(userId)),
    getResourceMappedList('notifications', String(userId)),
  ],
  (items, notifications) => notifications.map(event => ({
    key: event.id,
    activityText: getActivityText(
      event,
      items.find(item => item.id === event.trackable_id),
    ),
    time: getActivityTime(event),
  })),
);
